import { useGetStationDisplayName } from "@/entities/station";
import { getRouteStationNames, Rijtijd, TvvStandaardRoute } from "@/entities/tvv";
import { Badge, FormFieldInput, FormLabel, Icon, Input, MessageBar, SelectFormField, Slider } from "@/shared/components/ui";
import type { UseFormReturn } from "react-hook-form";
import type { BestellingenFormData } from "./schema";
import { useBestellingFormHandlers } from "./use-bestelling-form-handlers";

type BestellingFormSectionProps = {
    route: TvvStandaardRoute;
    index: number;
    form: UseFormReturn<BestellingenFormData>;
};

export function BestellingFormSection({ route, index, form }: Readonly<BestellingFormSectionProps>) {
    if (route.isOvRoute) {
        return <OVBestellingFormSection route={route} index={index} form={form} />;
    } else {
        return <RegularBestellingFormSection route={route} index={index} form={form} />;
    }
}

function RegularBestellingFormSection({ route, index, form }: Readonly<BestellingFormSectionProps>) {
    const getStationDisplayName = useGetStationDisplayName();
    const { beginStation, eindStation } = getRouteStationNames(route.stationCodes, getStationDisplayName);

    const {
        path,
        handleSliderChange,
        handleFrequentieChange,
        handleAantalBussenChange,
        handleBeginStationChange,
        handleEindStationChange,
        handleReisperiodeChange,
    } = useBestellingFormHandlers({ form, index, route });

    const reisperiodeOptions = route.periodeFactoren.map((pf) => ({
        label: pf.periode,
        value: pf.periode,
    }));

    const isAangepast = form.watch(`${path}.isAangepast`);
    const aantalBussen = form.watch(`${path}.aantalBussen`);
    const bussenBeginStation = form.watch(`${path}.bussenBeginStation`);

    const totaleRijtijd = Rijtijd.getTotaleRijtijd(route.rijtijden);

    return (
        <div className="space-y-4 rounded-lg border p-4">
            <div className="flex items-center gap-2">
                <h4 className="font-medium">Bestelling</h4>
                <Badge variant="blue-subtle">Rijtijd {Rijtijd.formatRijtijd(totaleRijtijd)}</Badge>
            </div>

            {reisperiodeOptions.length > 0 && (
                <SelectFormField
                    form={form}
                    name={`${path}.reisperiode`}
                    label="Vul op basis van:"
                    labelClassName="text-content-subtle font-normal"
                    options={reisperiodeOptions}
                    placeholder="Selecteer periode"
                    onValueChange={handleReisperiodeChange}
                    layout="inline"
                    suffix={isAangepast && <span className="text-content-subtle text-s italic">(aangepast)</span>}
                    className="flex-1"
                />
            )}

            <div className="flex items-end gap-4">
                <FormFieldInput
                    form={form}
                    name={`${path}.busFrequentie`}
                    label="Busfrequentie"
                    labelClassName="text-content-subtle"
                    type="number"
                    min={1}
                    max={50}
                    onValueChange={(value) => handleFrequentieChange(value as number)}
                    className="w-28"
                />
                <div className="mb-2 flex">
                    <Icon name="swap-horizontal" size="m" />
                </div>
                <FormFieldInput
                    form={form}
                    name={`${path}.aantalBussen`}
                    label="Aantal bussen"
                    labelClassName="text-content-subtle"
                    type="number"
                    min={1}
                    max={100}
                    onValueChange={(value) => handleAantalBussenChange(value as number)}
                    className="w-28"
                />
            </div>

            <div className="space-y-2">
                <div className="text-content-subtle text-s flex justify-between font-medium">
                    <FormLabel className="text-content-subtle" data-testid="beginstation">
                        {beginStation}
                    </FormLabel>
                    <FormLabel className="text-content-subtle" data-testid="eindstation">
                        {eindStation}
                    </FormLabel>
                </div>
                <div className="flex items-center gap-4">
                    <Input
                        type="number"
                        min={0}
                        max={aantalBussen}
                        value={bussenBeginStation}
                        onChange={(e) => handleBeginStationChange(parseInt(e.target.value) || 0)}
                        className="w-28"
                        data-testid="bussen-beginstation"
                    />
                    <Slider
                        value={[bussenBeginStation]}
                        min={0}
                        max={aantalBussen}
                        step={1}
                        onValueChange={handleSliderChange}
                        className="flex-1"
                    />
                    <Input
                        type="number"
                        min={0}
                        max={aantalBussen}
                        value={aantalBussen - bussenBeginStation}
                        onChange={(e) => handleEindStationChange(parseInt(e.target.value) || 0)}
                        className="w-28"
                        data-testid="bussen-eindstation"
                    />
                </div>
            </div>
        </div>
    );
}

function OVBestellingFormSection({ route, index, form }: Readonly<BestellingFormSectionProps>) {
    const { path } = useBestellingFormHandlers({ form, index, route });

    return (
        <div className="space-y-4 p-4">
            <FormFieldInput
                form={form}
                name={`${path}.ovLijnNummer`}
                label="Lijnnummer"
                placeholder="Lijn ..."
                helpText="Welke lijn kunnen reizigers nemen?"
                className="w-3/7"
            />
            <MessageBar>Voor OV-vervoer is geen specificatie van aantal bussen nodig.</MessageBar>
        </div>
    );
}
